import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getQuestion, addQuestion, updateQuestion, clearCurrentQuestion } from '../actions/questionActions';
import QuestionForm from '../components/content-management/QuestionForm';

import QuestionFormSkeleton from '../components/content-management/views/FormView/QuestionFormSkeleton';
import AdminLayout from '../components/settings/AdminLayout';
import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const QuestionEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentQuestion, questionLoading } = useSelector(state => state.question);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    const loadData = async () => {
      try {
        // If we have an ID, load the question
        if (id) {
          await dispatch(getQuestion(id));
        }
      } catch (error) {
        console.error('Error loading question:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentQuestion());
    };
  }, [dispatch, id]);

  const handleSubmit = async (formData) => {
    try {
      const action = id ? updateQuestion(id, formData) : addQuestion(formData);
      await dispatch(action);
      // Redirect back to questions list
      navigate('/settings', { state: { activeTab: 'Questions' } });
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const handleClose = () => {
    navigate('/settings', { state: { activeTab: 'Questions' } });
  };

  // Show loading state
  if (questionLoading && id) {
    return (
      <AdminLayout activeTab="Questions">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
            <button
              type="button"
              onClick={() => navigate('/settings', { state: { activeTab: 'Questions' } })}
              className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form Skeleton */}
          <QuestionFormSkeleton />
        </div>
      </AdminLayout>
    );
  }

  // If trying to edit/view a question that doesn't exist
  if (id && !currentQuestion && !questionLoading) {
    return (
      <AdminLayout activeTab="Questions">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-500 text-xl mb-4">Question not found</div>
          <button
            onClick={() => navigate('/settings', { state: { activeTab: 'Questions' } })}
            className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            Return to Questions
          </button>
        </div>
      </AdminLayout>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <AdminLayout activeTab="Questions">
      <QuestionForm
        mode={mode}
        question={currentQuestion}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </AdminLayout>
  );
};

export default QuestionEditor;
